/**
 * Who may open the Operator pages, read off GET /me.
 *
 * The service decides and the gateway carries its answer, exactly as the old
 * portal's sign-in did: an approved Son of Basel user is a seat, with their
 * role there, whatever their role in this app. GET /me answers 200 for a
 * seat and 403 `NO_SEAT` for a person with none. Nothing of React here, so it
 * is covered on its own.
 */
import { isOperatorError } from './api'
import type { OperatorMe } from './types'

export type OperatorAccess =
  | { state: 'loading' }
  | { state: 'allowed'; me: OperatorMe }
  /** Signed in, and this account has no seat. */
  | { state: 'no-access' }
  /** This app's session is gone. The sign-in screen is already on its way. */
  | { state: 'signed-out' }
  /** This server has no Operator service, or it did not answer. */
  | { state: 'unavailable'; message: string }
  | { state: 'failed'; message: string }

/** The sentence for a server with no Operator service behind it. */
export const NOT_SET_UP_MESSAGE = 'Operator is not set up on this server.'

/**
 * A seat lost or a session ended mid-visit wins over the answer cached from
 * before: react-query keeps the last good `/me` beside a failed refetch, and
 * reading that as allowed left every page polling a service that refuses it.
 * Any other failure (the service down for a moment) keeps the cached answer.
 */
export function accessFrom(query: { isPending: boolean; data?: OperatorMe | undefined; error?: unknown }): OperatorAccess {
  const error = query.error
  if (isOperatorError(error) && error.kind === 'no-access') return { state: 'no-access' }
  if (isOperatorError(error) && error.kind === 'signed-out') return { state: 'signed-out' }
  if (query.data !== undefined) return { state: 'allowed', me: query.data }
  if (query.isPending) return { state: 'loading' }
  if (isOperatorError(error)) {
    if (error.kind === 'no-access') return { state: 'no-access' }
    if (error.kind === 'signed-out') return { state: 'signed-out' }
    if (error.kind === 'not-found') return { state: 'unavailable', message: NOT_SET_UP_MESSAGE }
    if (error.kind === 'unreachable') return { state: 'unavailable', message: error.message }
    return { state: 'failed', message: error.message }
  }
  return { state: 'failed', message: error instanceof Error ? error.message : 'Operator could not be opened.' }
}

/**
 * Whether the sidebar draws the Operator rows: once GET /me says the person
 * has a seat. An admin of this app is drawn them while the answer is on its
 * way or the service cannot answer (so the page can say why), never once it
 * says they have no seat.
 */
export function showsOperator(isAdmin: boolean, access: OperatorAccess): boolean {
  if (access.state === 'allowed') return true
  return isAdmin && access.state !== 'no-access' && access.state !== 'signed-out'
}

/**
 * Whether the Team view of meetings widens for this person (the power admin,
 * or an approved admin in the service's own users). A service older than the
 * field answers without it, and then the role decides, as it did before.
 */
export function teamMeetingsOf(me: OperatorMe): boolean {
  return me.user.teamMeetings ?? (me.user.isPowerAdmin || me.user.role === 'admin')
}

// ---------------------------------------------------------------------------
// The ways in a person is offered, and the trip to onboarding
// ---------------------------------------------------------------------------

/**
 * What the sidebar, the rail and the palette offer this person in Operator.
 *
 *   seat        the module's rows (`showsOperator`)
 *   admin       an Operator admin (`me.user.role === 'admin'`, the stored
 *               Son of Basel role): the sandbox, Settings > Operator and its
 *               admin page. False until GET /me answers
 *   powerAdmin  the service's pinned power admin
 *   talk        Talk may be opened: the power admin always, anybody with a
 *               seat while the timed demo is open, as the old portal's Talk
 *               route allowed and its nav promised ("gate it on isPowerAdmin")
 *
 * Tidiness: every route asks the service again, and the service refuses.
 */
export interface OperatorReach {
  seat: boolean
  admin: boolean
  powerAdmin: boolean
  talk: boolean
}

export const NO_OPERATOR_REACH: OperatorReach = { seat: false, admin: false, powerAdmin: false, talk: false }

export function operatorReachOf(isAdmin: boolean, access: OperatorAccess, talkOpen = false): OperatorReach {
  const seat = showsOperator(isAdmin, access)
  if (access.state !== 'allowed') return { seat, admin: false, powerAdmin: false, talk: false }
  const powerAdmin = access.me.user.isPowerAdmin === true
  return { seat, admin: access.me.user.role === 'admin', powerAdmin, talk: powerAdmin || talkOpen }
}

/**
 * Whether GET /talk/access says the demo is open at `now`. The service works
 * the window out itself (`enabled` is false once it has run out), and the
 * expiry is checked again here so a cached answer closes on time.
 */
export function talkIsOpen(answer: unknown, now: number): boolean {
  if (typeof answer !== 'object' || answer === null) return false
  const { enabled, expiresAt } = answer as { enabled?: unknown; expiresAt?: unknown }
  if (enabled !== true) return false
  return typeof expiresAt !== 'number' || expiresAt <= 0 || expiresAt > now
}

/** The one page that sends a person with no clone to onboarding: Live, the old portal's home page. */
export const ONBOARDING_GATED_PATH = '/operator/live'

/**
 * Whether this page sends the person to onboarding. Only Live does, as only
 * the old portal's home page did (`useOnboardingGate` in `app/page.tsx`):
 * every other page, Settings > Operator and its admin page open for a person
 * with no clone yet. A draft clone (onboarding left for later) is not sent.
 */
export function sendsToOnboarding(access: OperatorAccess, pathname: string): boolean {
  if (access.state !== 'allowed' || !access.me.needsOnboarding) return false
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return path === ONBOARDING_GATED_PATH
}

/** What a person who is not an Operator admin sees on an Operator admin page (the sandbox). */
export const OPERATOR_ADMINS_ONLY_TITLE = 'This page is for admins'
export const OPERATOR_ADMINS_ONLY_COPY = 'The sandbox tests models and voices for every clone, so it is limited to Operator admins. Ask an admin if you need something tried.'
